/**
 * PortfolioRiskChart: a dashboard panel that reads a portfolio's risk level
 * and volatility at a glance — the qualitative rating, annualized volatility
 * and max drawdown, concentration/liquidity gauges, and the recorded value
 * trend. Uses the first portfolio the user owns (the Risk dashboard is where
 * you switch between them).
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { Portfolio, PortfolioRiskAssessment } from "@/types";
import LineChart from "@/components/charts/LineChart";

interface Snapshot {
  as_of_date: string;
  total_value: number;
}

const RATING_COPY: Record<PortfolioRiskAssessment["overall_rating"], { label: string; tone: string; bar: string }> = {
  low: { label: "Low risk", tone: "text-signal-teal", bar: "bg-signal-teal" },
  moderate: { label: "Moderate risk", tone: "text-mist-200", bar: "bg-mist-300" },
  elevated: { label: "Elevated risk", tone: "text-signal-amber", bar: "bg-signal-amber" },
  high: { label: "High risk", tone: "text-signal-rose", bar: "bg-signal-rose" },
} as const;

const UNKNOWN_RATING: { label: string; tone: string; bar: string } = {
  label: "Unknown risk",
  tone: "text-mist-400",
  bar: "bg-mist-500",
};

function ratingCopy(rating: string | null | undefined): { label: string; tone: string; bar: string } {
  return (rating && RATING_COPY[rating as PortfolioRiskAssessment["overall_rating"]]) || UNKNOWN_RATING;
}

/**
 * Format a risk metric safely. The TypeScript type says `number | null`, but
 * a field that's missing from the actual JSON response comes through as
 * `undefined` at runtime, not `null` — and `undefined !== null` is true in
 * JS, so a guard that only checks for `null` lets `undefined` slip through
 * and crash on `.toFixed()`. This treats both the same, and also coerces
 * with Number() in case a value ever arrives as a string.
 */
function fmtNum(value: number | string | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(decimals) : "—";
}

function Meter({
  label,
  value,
  max,
  tone,
}: {
  label: string;
  value: number | string | null | undefined;
  max: number;
  tone: string;
}): React.ReactElement {
  const num = value === null || value === undefined ? NaN : Number(value);
  const pct = Number.isFinite(num) ? Math.max(0, Math.min(100, (num / max) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-mist-400">
        <span>{label}</span>
        <span className="font-mono text-mist-200">{fmtNum(value)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-ink-800">
        <div
          className={`h-full origin-left animate-bar-grow rounded-full ${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function PortfolioRiskChart(): React.ReactElement {
  const [name, setName] = useState<string | null>(null);
  const [risk, setRisk] = useState<PortfolioRiskAssessment | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [state, setState] = useState<"loading" | "empty" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: portfolios } = await apiClient.get<Portfolio[]>("/portfolios");
        if (cancelled) return;
        if (portfolios.length === 0) {
          setState("empty");
          return;
        }
        const portfolio = portfolios[0];
        setName(portfolio.name);

        const [riskRes, snapshotRes] = await Promise.all([
          apiClient.get<PortfolioRiskAssessment>(`/portfolios/${portfolio.id}/risk`).catch(() => null),
          apiClient.get<Snapshot[]>(`/portfolios/${portfolio.id}/snapshots`).catch(() => null),
        ]);
        if (cancelled) return;
        setRisk(riskRes?.data ?? null);
        setSnapshots(Array.isArray(snapshotRes?.data) ? snapshotRes.data : []);
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const values = Array.isArray(snapshots)
    ? snapshots
        .slice()
        .reverse()
        .map((snapshot) => Number(snapshot.total_value))
        .filter((value) => Number.isFinite(value))
    : [];

  return (
    <div className="panel p-5">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-mist-100">Portfolio risk &amp; volatility</h2>
        {name && <span className="text-xs text-mist-500">{name}</span>}
      </div>

      {state === "loading" && <p className="mt-3 text-sm text-mist-400">Loading risk…</p>}
      {state === "empty" && (
        <p className="mt-3 text-sm text-mist-400">Create a portfolio and add positions to see its risk profile.</p>
      )}
      {state === "error" && <p className="mt-3 text-sm text-signal-rose">Unable to load portfolio risk.</p>}

      {state === "ready" && risk && (
        <div className="mt-3 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="flex items-center justify-between">
              <span className={`text-lg font-semibold ${ratingCopy(risk.overall_rating).tone}`}>
                {ratingCopy(risk.overall_rating).label}
              </span>
              <span className="font-mono text-xs text-mist-400">
                {risk.volatility_annualized_pct === null || risk.volatility_annualized_pct === undefined
                  ? "vol —"
                  : `${fmtNum(risk.volatility_annualized_pct, 1)}% vol`}
              </span>
            </div>
            <div className="mt-4 space-y-3">
              <Meter
                label="Volatility (ann. %)"
                value={risk.volatility_annualized_pct}
                max={60}
                tone={ratingCopy(risk.overall_rating).bar}
              />
              <Meter label="Max drawdown (%)" value={risk.max_drawdown_pct} max={60} tone="bg-signal-rose" />
              <Meter label="Concentration (HHI)" value={risk.concentration_hhi} max={10000} tone="bg-signal-amber" />
              <Meter label="Liquidity score" value={risk.liquidity_risk_score} max={100} tone="bg-signal-teal" />
            </div>
          </div>

          <div>
            <p className="text-xs text-mist-400">Recorded value trend</p>
            {values.length > 1 ? (
              <div className="mt-2 overflow-x-auto">
                <LineChart data={values} width={480} height={130} />
              </div>
            ) : (
              <p className="mt-2 text-sm text-mist-400">
                No snapshots recorded yet — value history appears here as snapshots build up.
              </p>
            )}
            {Array.isArray(risk.notes) && risk.notes.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-mist-500">
                {risk.notes.slice(0, 3).map((note, index) => (
                  <li key={index}>• {note}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
